// Backend integration point: replace localStorage calls with a server-side session/DB for cross-device sync

export interface GameStats {
  currentStreak: number;
  lifetimeKills: number;
  allTimeBest: number;
  lastPlayed: string;
}

const STATS_KEY = 'unbored_stats_v2';

export function getStats(): GameStats {
  if (typeof window === 'undefined') {
    return { currentStreak: 0, lifetimeKills: 0, allTimeBest: 0, lastPlayed: '' };
  }
  try {
    const raw = localStorage.getItem(STATS_KEY);
    if (!raw) return { currentStreak: 0, lifetimeKills: 0, allTimeBest: 0, lastPlayed: '' };
    return JSON.parse(raw) as GameStats;
  } catch {
    return { currentStreak: 0, lifetimeKills: 0, allTimeBest: 0, lastPlayed: '' };
  }
}

export function saveStats(stats: GameStats): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STATS_KEY, JSON.stringify(stats));
}

export function incrementStreak(): GameStats {
  const stats = getStats();
  const newStreak = stats.currentStreak + 1;
  const newBest = Math.max(stats.allTimeBest, newStreak);
  const updated: GameStats = {
    ...stats,
    currentStreak: newStreak,
    lifetimeKills: stats.lifetimeKills + 1,
    allTimeBest: newBest,
    lastPlayed: new Date().toISOString(),
  };
  saveStats(updated);
  return updated;
}

export function resetStreak(): GameStats {
  const stats = getStats();
  const updated: GameStats = {
    ...stats,
    currentStreak: 0,
    lifetimeKills: stats.lifetimeKills + 1,
    lastPlayed: new Date().toISOString(),
  };
  saveStats(updated);
  return updated;
}

export function clearCurrentStreak(): void {
  const stats = getStats();
  saveStats({ ...stats, currentStreak: 0 });
}

// Backend integration point: replace with API call to increment global counter in DB
export function getGlobalCounter(): number {
  if (typeof window === 'undefined') return 847293;
  const key = 'unbored_global_today';
  const raw = localStorage.getItem(key);
  if (!raw) {
    const seed = 847293;
    localStorage.setItem(key, JSON.stringify({ count: seed, date: new Date().toDateString() }));
    return seed;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed.date !== new Date().toDateString()) {
      const newCount = Math.floor(Math.random() * 50000) + 200000;
      localStorage.setItem(key, JSON.stringify({ count: newCount, date: new Date().toDateString() }));
      return newCount;
    }
    return parsed.count;
  } catch {
    return 847293;
  }
}

export function incrementGlobalCounter(): number {
  if (typeof window === 'undefined') return 847293;
  const key = 'unbored_global_today';
  const raw = localStorage.getItem(key);
  let current = 847293;
  try {
    if (raw) {
      const parsed = JSON.parse(raw);
      current = parsed.count || 847293;
    }
  } catch { /* noop */ }
  const newCount = current + 1;
  localStorage.setItem(key, JSON.stringify({ count: newCount, date: new Date().toDateString() }));
  return newCount;
}

const MUTE_KEY = 'unbored_muted';
export function getMuted(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(MUTE_KEY) === 'true';
}
export function setMuted(val: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(MUTE_KEY, String(val));
}